import { createContext, useContext } from 'react';
import type { CounterView, TableModel, ZoneView } from '../model/table';
import { seatsFor } from './repeat';
export { seatsFor } from './repeat';
import type { LogEntry } from '../session/Session';
import type { Board } from '../protocol/ui';
import type { BrickInstance, PresentationV2 } from '../presentation/types';
import { parseSelector, selectCounters, selectZones } from '../presentation/selectors';
import { BRICKS } from '../bricks/schema';

export interface SceneApi {
  table: TableModel;
  presentation: PresentationV2;
  seatColor: (seat: string | null) => string;
  seatLabel: (seat: string | null) => string;
  log: LogEntry[];
  /** Last public log line per seat (for opponent strips). */
  lastAction: Map<string, string>;
  /** Board zone and its Describe board by board ID. */
  board: (id: string) => { zone: ZoneView; board: Board } | null;
  speed: number;
  setMotionLimit?:(maximum:number|undefined)=>void;
  /** Replay or disconnected displays must not reuse a previous live activity on tab mount. */
  activitySuspended?: boolean;
  setSpeed: (pace: number) => void;
  /** The turn prompt sits in the HUD; the HUD registers its slot through `promptHost`. */
  headerPrompt?: boolean;
  /** The opponent turn banner on show; a scene with views draws it over the shown tab. */
  banner?: { text: string; color: string; key: number } | null;
  promptHost?: (el: HTMLElement | null) => void;
}

const SceneContext = createContext<SceneApi | null>(null);
export const SceneProvider = SceneContext.Provider;
export function useScene(): SceneApi {
  const s = useContext(SceneContext);
  if (!s) throw new Error('Bricks render inside a scene.');
  return s;
}

/** Optional scene access for shared primitives that also render outside presentation-2 scenes. */
export function useSceneOptional(): SceneApi | null { return useContext(SceneContext); }

/** Substitutes `{self}` and `{seat}` in a brick's seat reference. */
export function seatOf(inst: BrickInstance, seat: string | null, self: string | null): string | null {
  if (!inst.seat) return seat;
  if (inst.seat === '{self}') return self;
  if (inst.seat === '{seat}') return seat;
  return inst.seat;
}

export function zonesFor(inst: BrickInstance, table: TableModel, seat: string | null, placed = new Set<string>()): ZoneView[] {
  return inst.select ? zonesOf(inst.select, table, seat, placed) : [];
}

/** Zones a selector names (a `select` or a selector prop such as `occupantsFrom`). */
export function zonesOf(selector: string, table: TableModel, seat: string | null, placed = new Set<string>()): ZoneView[] {
  const sel = parseSelector(selector);
  if (typeof sel === 'string') return [];
  return selectZones(sel, table.zones, seat, table.selfSeat, placed);
}

export function countersFor(inst: BrickInstance, table: TableModel, seat: string | null): CounterView[] {
  if (!inst.select) return [];
  const sel = parseSelector(inst.select);
  if (typeof sel === 'string') return [];
  return selectCounters(sel, table.counters, seat, table.selfSeat);
}

/** Zones a scene places through any selector other than `rest`, for the given table. */
export function placedZones(instances: BrickInstance[], table: TableModel): Set<string> {
  const placed = new Set<string>();
  const visit = (inst: BrickInstance, seat: string | null) => {
    if (inst.brick === 'repeat') { for (const s of seatsFor(inst, table.seats, table)) if (inst.child) visit(inst.child, s.id); return; }
    const own = seatOf(inst, seat, table.selfSeat);
    // An opponent strip shows the counts of the seat's zones whose contents this viewer cannot see.
    if (inst.brick === 'opponent-strip' && own) for (const z of table.zones) {
      const kinds = inst.props?.zoneKinds as string[] | undefined;
      if (z.owner === own && z.kind !== 'board' && z.visibility !== 'public' && z.objects.length === 0 && (!kinds || kinds.includes(z.kind))) placed.add(z.id);
    }
    if (inst.select && inst.select !== 'rest') for (const z of zonesFor(inst, table, own)) placed.add(z.id);
    // A HUD that shows pile counts shows the public decks and discards.
    if (inst.brick === 'hud' && (inst.props?.show as string[] | undefined)?.includes('piles')) for (const z of table.zones) if (z.owner === null && (z.kind === 'stack' || z.kind === 'discard')) placed.add(z.id);
    // A HUD that shows the viewer's own piles shows their decks and discards.
    if (inst.brick === 'hud' && (inst.props?.show as string[] | undefined)?.includes('self-piles') && table.selfSeat) for (const z of table.zones) if (z.owner === table.selfSeat && (z.kind === 'stack' || z.kind === 'discard')) placed.add(z.id);
    if (inst.select?.startsWith('board:')) for (const z of zonesFor(inst, table, own)) placed.add(z.id);
    // Selector props (occupants, chips, used pieces) draw those zones' objects too; link selectors only name them.
    const schema = BRICKS[inst.brick];
    for (const [key, value] of Object.entries(inst.props ?? {})) if (schema?.props[key]?.type === 'selector' && !(schema.props[key] as { links?: true }).links && typeof value === 'string') for (const z of zonesOf(value, table, own)) placed.add(z.id);
    const slots = inst.props?.slots;
    if (Array.isArray(slots)) for (const child of slots as BrickInstance[]) visit(child, own);
  };
  for (const inst of instances) visit(inst, null);
  return placed;
}
